import React, { useCallback, ComponentProps } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import MUIRadio from '@mui/material/Radio';
import MUIRadioGroup from '@mui/material/RadioGroup';
import MUIFormControlLabel from '@mui/material/FormControlLabel';
import { Controller } from 'react-hook-form';
import overlay from '../Palettes/overlay';

const RadioGroup = styled(MUIRadioGroup)`
  .MuiRadio-colorSecondary {
    color: ${overlay[600]};
  }

  .MuiRadio-colorSecondary.Mui-checked {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .MuiFormControlLabel-label {
    color: ${overlay[600]};
    transform: none;
    letter-spacing: unset;
    margin: 2px 0 0px;
  }

  .MuiButtonBase-root {
    padding: 3px;
  }
`;

const radioButtonsPropertyTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

export type RadioButtonsProperties = InferProps<
  typeof radioButtonsPropertyTypes
>;

const RadioButtons = ({
  values,
  name,
  value,
  onChange,
}: RadioButtonsProperties) => (
  <RadioGroup
    aria-label={name}
    name={name}
    value={value}
    onChange={onChange}
  >
    {
      values.map(({ value: optionValue, label }) => (
        <MUIFormControlLabel
          key={optionValue}
          control={<MUIRadio />}
          value={optionValue}
          label={label}
        />
      ))
    }
  </RadioGroup>
);

RadioButtons.propTypes = radioButtonsPropertyTypes;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const radioButtonsWithControllerPropertyTypes = {
  control: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.shape({}).isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

type RadioButtonsWithControllerProperties = InferProps<
  typeof radioButtonsWithControllerPropertyTypes
> & {
  control: ComponentProps<typeof Controller>['control'];
  name: string;
  rules: ComponentProps<typeof Controller>['rules'];
  values: { value: string; label: string }[];
};

export const RadioButtonsWithController = ({
  control,
  name,
  rules,
  values,
}: RadioButtonsWithControllerProperties) => {
  const render = useCallback(
    ({
      onChange, value,
    }: {
      // eslint-disable-next-line react/no-unused-prop-types
      onChange: (value: boolean) => void;
      // eslint-disable-next-line react/no-unused-prop-types
      value: boolean;
    }) => (
      <RadioButtons
        name={name}
        onChange={
          (
            { target: { value: selectedValue } },
          ) => onChange(selectedValue === 'true')
        }
        value={String(value)}
        values={values}
      />
    ),
    [name, values],
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={render}
    />
  );
};

export default RadioButtons;
